<script setup>
import { ACTION_TYPES } from 'dashboard/helper/captain/agentActionTypes';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

defineProps({
  actions: { type: Array, default: () => [] },
  actionTypeLabels: { type: Object, default: () => ({}) },
  linkedScenarioCount: { type: Function, required: true },
  isActionApprovalLocked: { type: Function, required: true },
  savingAvailabilityId: { type: [Number, String], default: null },
  savingActionId: { type: [Number, String], default: null },
  canManage: { type: Boolean, default: false },
  hasAdvancedApprovals: { type: Boolean, default: false },
});

const emit = defineEmits([
  'openScenarios',
  'toggleAvailability',
  'toggleApproval',
]);
const search = defineModel('search', { type: String, default: '' });
</script>

<template>
  <section class="flex flex-col gap-4">
    <div
      class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <SettingsHeader
        :heading="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ACTIONS_TITLE')"
        :description="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ACTIONS_DESCRIPTION')"
      />
      <Button
        type="button"
        :label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.OPEN_SCENARIOS')"
        icon="i-lucide-route"
        slate
        sm
        class="w-full sm:w-auto"
        @click="emit('openScenarios')"
      />
    </div>

    <Input
      v-model="search"
      :placeholder="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.SEARCH')"
      :aria-label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.SEARCH')"
      class="w-full sm:max-w-md"
    />

    <div class="flex flex-col gap-3">
      <CardLayout v-for="action in actions" :key="action.id">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ action.name }}
            </h3>
            <p class="mt-1 text-sm leading-relaxed text-n-slate-11">
              {{ action.description }}
            </p>
          </div>
          <span
            class="rounded-full bg-n-blue-3 px-2 py-1 text-xs font-medium text-n-blue-11"
          >
            {{ actionTypeLabels[action.risk_level] || action.risk_level }}
          </span>
        </div>
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <p class="text-xs text-n-slate-10">
            {{
              $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.LINKED_SCENARIOS', {
                count: linkedScenarioCount(action),
              })
            }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              v-if="!action.custom"
              type="button"
              color="slate"
              size="xs"
              variant="faded"
              :icon="
                action.enabled
                  ? 'i-lucide-toggle-right'
                  : 'i-lucide-toggle-left'
              "
              :label="
                action.enabled
                  ? $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ACTION_ENABLED')
                  : $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ACTION_DISABLED')
              "
              :is-loading="savingAvailabilityId === action.id"
              :disabled="!canManage"
              @click="emit('toggleAvailability', action)"
            />
            <span v-else class="text-xs text-n-slate-11">
              {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_MANAGED_BELOW') }}
            </span>
            <Button
              v-if="
                hasAdvancedApprovals && action.risk_level !== ACTION_TYPES.READ
              "
              type="button"
              color="slate"
              size="xs"
              variant="faded"
              :icon="
                action.approval_required
                  ? 'i-lucide-shield-check'
                  : 'i-lucide-shield'
              "
              :label="action.permission"
              :is-loading="savingActionId === action.id"
              :disabled="!canManage || isActionApprovalLocked(action)"
              @click="emit('toggleApproval', action)"
            />
          </div>
        </div>
      </CardLayout>
    </div>

    <p
      v-if="actions.length === 0"
      class="rounded-xl border border-dashed border-n-weak p-5 text-center text-sm text-n-slate-11"
    >
      {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.NO_ACTIONS') }}
    </p>
  </section>
</template>
